// src/pages/Admission/Admission.js
import React, { useState } from 'react';
import './Admission.css';

const Admission = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    childName: '',
    childAge: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const programs = [
    { value: 'playgroup', label: 'Playgroup (2-3 Years)' },
    { value: 'pre-nursery', label: 'Pre-Nursery (3-4 Years)' },
    { value: 'nursery', label: 'Nursery (4-5 Years)' },
    { value: 'kindergarten', label: 'Kindergarten (5-6 Years)' },
    { value: 'daycare', label: 'Daycare' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Show success message
    setShowSuccess(true);
    
    // Clear form
    setFormData({
      parentName: '',
      childName: '',
      childAge: '',
      email: '',
      phone: '',
      program: '',
      message: ''
    });

    // Hide success message after 5 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 5000);
  };

  return (
    <div className="admission-page page-content">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">Admissions</h1>
          <p className="page-subtitle">Join the DisneyLand Family - Enroll Your Child Today</p>
        </div>
      </section>

      {/* Admission Process */}
      <section className="admission-process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Admission Process</h2>
            <div className="title-underline"></div>
          </div>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-icon">📝</div>
              <h3>Fill Application</h3>
              <p>Complete the online admission form with your child's details</p>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-icon">🏫</div>
              <h3>Visit Campus</h3>
              <p>Schedule a visit to tour our facilities and meet our teachers</p>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-icon">📋</div>
              <h3>Submit Documents</h3>
              <p>Provide necessary documents and complete registration</p>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-icon">🎉</div>
              <h3>Welcome to DisneyLand</h3>
              <p>Your child is ready to start their learning journey!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs and Age Groups */}
      <section className="programs-age-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Programs</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">Choose the right program for your child's age group</p>
          </div>

          <div className="programs-table-wrapper">
            <table className="programs-table">
              <thead>
                <tr>
                  <th>Program</th>
                  <th>Age Group</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Playgroup</td>
                  <td>2 – 3 Years</td>
                  <td>3 Hours/Day</td>
                </tr>
                <tr>
                  <td>Pre-Nursery</td>
                  <td>3 – 4 Years</td>
                  <td>3 Hours/Day</td>
                </tr>
                <tr>
                  <td>Nursery</td>
                  <td>4 – 5 Years</td>
                  <td>3 Hours/Day</td>
                </tr>
                <tr>
                  <td>Kindergarten</td>
                  <td>5 – 6 Years</td>
                  <td>4 Hours/Day</td>
                </tr>
                <tr>
                  <td>Daycare</td>
                  <td>All Ages</td>
                  <td>Full Day</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="note-text">
            <strong>Note:</strong> As per RTE (Right to Education) and NEP (National Education Policy) from state to state.
          </p>
        </div>
      </section>

      {/* Admission Form */}
      <section className="admission-form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-intro">
              <h2>Apply for Admission</h2>
              <p>Fill out the form below and we'll get back to you soon to schedule a campus visit.</p>
            </div>
            
            {showSuccess && (
              <div className="success-message">
                ✓ Details submitted successfully! We will contact you soon.
              </div>
            )}

            <form onSubmit={handleSubmit} className="admission-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="parentName">Parent/Guardian Name *</label>
                  <input
                    type="text"
                    id="parentName"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    required
                    placeholder="Enter parent/guardian name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="childName">Child's Name *</label>
                  <input
                    type="text"
                    id="childName"
                    name="childName"
                    value={formData.childName}
                    onChange={handleChange}
                    required
                    placeholder="Enter child's name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="childAge">Child's Age *</label>
                  <input
                    type="number"
                    id="childAge"
                    name="childAge"
                    value={formData.childAge}
                    onChange={handleChange}
                    required
                    min="1"
                    max="10"
                    placeholder="Enter child's age"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="program">Program *</label>
                  <select
                    id="program"
                    name="program"
                    value={formData.program}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a program</option>
                    {programs.map(prog => (
                      <option key={prog.value} value={prog.value}>{prog.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter email address"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Additional Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Any specific questions or requirements?"
                ></textarea>
              </div>

              <button type="submit" className="submit-btn">
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="documents-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Documents Required</h2>
            <div className="title-underline"></div>
          </div>
          <div className="documents-grid">
            <div className="document-card">
              <div className="doc-icon">📄</div>
              <h3>Birth Certificate</h3>
              <p>Original and photocopy of child's birth certificate</p>
            </div>
            <div className="document-card">
              <div className="doc-icon">📸</div>
              <h3>Photographs</h3>
              <p>4 recent passport size photographs of the child</p>
            </div>
            <div className="document-card">
              <div className="doc-icon">🆔</div>
              <h3>Parent ID Proof</h3>
              <p>Aadhar card / Passport / Driving license copy</p>
            </div>
            <div className="document-card">
              <div className="doc-icon">🏠</div>
              <h3>Address Proof</h3>
              <p>Utility bill or rental agreement</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admission;









// // src/pages/Admission/Admission.js
// import React, { useState } from 'react';
// import './Admission.css';

// const Admission = () => {
//   const [formData, setFormData] = useState({
//     parentName: '',
//     childName: '',
//     childAge: '',
//     email: '',
//     phone: '',
//     program: '',
//     message: ''
//   });

//   const [showSuccess, setShowSuccess] = useState(false);

//   const programs = [
//     { value: 'playgroup', label: 'Playgroup (2-3 Years)' },
//     { value: 'pre-nursery', label: 'Pre-Nursery (3-4 Years)' },
//     { value: 'nursery', label: 'Nursery (4-5 Years)' },
//     { value: 'kindergarten', label: 'Kindergarten (5-6 Years)' },
//     { value: 'daycare', label: 'Daycare' }
//   ];

//   const handleChange = (e) => {
//     setFormData({
//       ...formData,
//       [e.target.name]: e.target.value
//     });
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
    
//     // Show success message
//     setShowSuccess(true);
    
//     // Clear form
//     setFormData({
//       parentName: '',
//       childName: '',
//       childAge: '',
//       email: '',
//       phone: '',
//       program: '',
//       message: ''
//     });

//     // Hide success message after 5 seconds
//     setTimeout(() => {
//       setShowSuccess(false);
//     }, 5000);
//   };

//   return (
//     <div className="admission-page page-content">
//       {/* Page Header */}
//       <section className="page-header">
//         <div className="container">
//           <h1 className="page-title">Admissions</h1>
//           <p className="page-subtitle">Join the DisneyLand Family - Enroll Your Child Today</p>
//         </div>
//       </section>

//       {/* Admission Process */}
//       <section className="admission-process-section">
//         <div className="container">
//           <div className="section-header">
//             <h2 className="section-title">Admission Process</h2>
//             <div className="title-underline"></div>
//           </div>
//           <div className="process-steps">
//             <div className="process-step">
//               <div className="step-number">1</div>
//               <div className="step-icon">📝</div>
//               <h3>Fill Application</h3>
//               <p>Complete the online admission form with your child's details</p>
//             </div>
//             <div className="process-arrow">→</div>
//             <div className="process-step">
//               <div className="step-number">2</div>
//               <div className="step-icon">🏫</div>
//               <h3>Visit Campus</h3>
//               <p>Schedule a visit to tour our facilities and meet our teachers</p>
//             </div>
//             <div className="process-arrow">→</div>
//             <div className="process-step">
//               <div className="step-number">3</div>
//               <div className="step-icon">📋</div>
//               <h3>Submit Documents</h3>
//               <p>Provide necessary documents and complete registration</p>
//             </div>
//             <div className="process-arrow">→</div>
//             <div className="process-step">
//               <div className="step-number">4</div>
//               <div className="step-icon">🎉</div>
//               <h3>Welcome to DisneyLand</h3>
//               <p>Your child is ready to start their learning journey!</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Programs and Age Groups */}
//       <section className="programs-age-section">
//         <div className="container">
//           <div className="section-header">
//             <h2 className="section-title">Our Programs</h2>
//             <div className="title-underline"></div>
//             <p className="section-subtitle">Choose the right program for your child's age group</p>
//           </div>
//           <div className="programs-table">
//             <div className="program-row program-header">
//               <div className="program-col">Program</div>
//               <div className="program-col">Age Group</div>
//               <div className="program-col">Duration</div>
//             </div>
//             <div className="program-row">
//               <div className="program-col">
//                 <span className="program-icon">🎈</span>
//                 <strong>Playgroup</strong>
//               </div>
//               <div className="program-col">2 - 3 Years</div>
//               <div className="program-col">3 Hours/Day</div>
//             </div>
//             <div className="program-row">
//               <div className="program-col">
//                 <span className="program-icon">🎨</span>
//                 <strong>Pre-Nursery</strong>
//               </div>
//               <div className="program-col">3 - 4 Years</div>
//               <div className="program-col">3 Hours/Day</div>
//             </div>
//             <div className="program-row">
//               <div className="program-col">
//                 <span className="program-icon">📚</span>
//                 <strong>Nursery</strong>
//               </div>
//               <div className="program-col">4 - 5 Years</div>
//               <div className="program-col">3 Hours/Day</div>
//             </div>
//             <div className="program-row">
//               <div className="program-col">
//                 <span className="program-icon">🎓</span>
//                 <strong>Kindergarten</strong>
//               </div>
//               <div className="program-col">5 - 6 Years</div>
//               <div className="program-col">4 Hours/Day</div>
//             </div>
//             <div className="program-row">
//               <div className="program-col">
//                 <span className="program-icon">🏡</span>
//                 <strong>Daycare</strong>
//               </div>
//               <div className="program-col">All Ages</div>
//               <div className="program-col">Full Day</div>
//             </div>
//           </div>
//           <p className="note-text">
//             <strong>Note:</strong> As per RTE (Right to Education) and NEP (National Education Policy) from state to state.
//           </p>
//         </div>
//       </section>

//       {/* Admission Form */}
//       <section className="admission-form-section">
//         <div className="container">
//           <div className="form-wrapper">
//             <div className="form-intro">
//               <h2>Apply for Admission</h2>
//               <p>Fill out the form below and we'll get back to you soon to schedule a campus visit.</p>
//             </div>
            
//             {showSuccess && (
//               <div className="success-message">
//                 ✓ Details submitted successfully! We will contact you soon.
//               </div>
//             )}

//             <form onSubmit={handleSubmit} className="admission-form">
//               <div className="form-row">
//                 <div className="form-group">
//                   <label htmlFor="parentName">Parent/Guardian Name *</label>
//                   <input
//                     type="text"
//                     id="parentName"
//                     name="parentName"
//                     value={formData.parentName}
//                     onChange={handleChange}
//                     required
//                     placeholder="Enter parent/guardian name"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="childName">Child's Name *</label>
//                   <input
//                     type="text"
//                     id="childName"
//                     name="childName"
//                     value={formData.childName}
//                     onChange={handleChange}
//                     required
//                     placeholder="Enter child's name"
//                   />
//                 </div>
//               </div>

//               <div className="form-row">
//                 <div className="form-group">
//                   <label htmlFor="childAge">Child's Age *</label>
//                   <input
//                     type="number"
//                     id="childAge"
//                     name="childAge"
//                     value={formData.childAge}
//                     onChange={handleChange}
//                     required
//                     min="1"
//                     max="10"
//                     placeholder="Enter child's age"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="program">Program *</label>
//                   <select
//                     id="program"
//                     name="program"
//                     value={formData.program}
//                     onChange={handleChange}
//                     required
//                   >
//                     <option value="">Select a program</option>
//                     {programs.map(prog => (
//                       <option key={prog.value} value={prog.value}>{prog.label}</option>
//                     ))}
//                   </select>
//                 </div>
//               </div>

//               <div className="form-row">
//                 <div className="form-group">
//                   <label htmlFor="email">Email Address *</label>
//                   <input
//                     type="email"
//                     id="email"
//                     name="email"
//                     value={formData.email}
//                     onChange={handleChange}
//                     required
//                     placeholder="Enter email address"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="phone">Phone Number *</label>
//                   <input
//                     type="tel"
//                     id="phone"
//                     name="phone"
//                     value={formData.phone}
//                     onChange={handleChange}
//                     required
//                     placeholder="Enter phone number"
//                   />
//                 </div>
//               </div>

//               <div className="form-group">
//                 <label htmlFor="message">Additional Message (Optional)</label>
//                 <textarea
//                   id="message"
//                   name="message"
//                   value={formData.message}
//                   onChange={handleChange}
//                   rows="4"
//                   placeholder="Any specific questions or requirements?"
//                 ></textarea>
//               </div>

//               <button type="submit" className="submit-btn">
//                 Submit Application
//               </button>
//             </form>
//           </div>
//         </div>
//       </section>

//       {/* Documents Required */}
//       <section className="documents-section">
//         <div className="container">
//           <div className="section-header">
//             <h2 className="section-title">Documents Required</h2>
//             <div className="title-underline"></div>
//           </div>
//           <div className="documents-grid">
//             <div className="document-card">
//               <div className="doc-icon">📄</div>
//               <h3>Birth Certificate</h3>
//               <p>Original and photocopy of child's birth certificate</p>
//             </div>
//             <div className="document-card">
//               <div className="doc-icon">📸</div>
//               <h3>Photographs</h3>
//               <p>4 recent passport size photographs of the child</p>
//             </div>
//             <div className="document-card">
//               <div className="doc-icon">🆔</div>
//               <h3>Parent ID Proof</h3>
//               <p>Aadhar card / Passport / Driving license copy</p>
//             </div>
//             <div className="document-card">
//               <div className="doc-icon">🏠</div>
//               <h3>Address Proof</h3>
//               <p>Utility bill or rental agreement</p>
//             </div>
//           </div>
//         </div>
//       </section>
//     </div>
//   );
// };

// export default Admission;